import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, MapPin, Target, Settings, Calculator, Shield, Users } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import OpeningHoursSelector from '../../components/ui/OpeningHoursSelector';
import { offerService, CreateOfferData, Offer } from '../../services/offerService';
import { getAllPartners, Partner } from '../../services/partnerService';
import { useAuth } from '../../contexts/AuthContext';

// Types for Admin Create Offer
interface AdminCreateOfferFormData {
  offerType: 'restaurant' | 'attraction';
  title: string;
  description: string;
  category: string;
  partnerId: string;
  address: string;
  regularPrice: number;
  discountType: 'percentage' | 'amount' | 'free_goodie' | 'one_plus_one';
  discountPercentage: number;
  discountAmount: number;
  freeGoodieDescription: string;
  redemptionMode: 'qr_upload' | 'barcode_upload' | 'auto_generate' | 'custom_code' | 'url';
  customRedemptionCode: string;
  redemptionUrl: string;
  redemptionType: 'amount' | 'unlimited' | 'user_limit';
  maxRedemptions: number;
  userRedemptionLimit: number;
  userRedemptionResetDays: number;
  operatingHours: string;
  startDate: string;
  endDate: string;
  headerImage: string;
  logoImage: string;
  barcodeFile: File | null;
  qrFile: File | null;
  customTermsConditions: string;
  // Admin-specific fields
  isFeatured: boolean;
  systemWidePromotion: boolean;
  priorityLevel: 'normal' | 'high' | 'urgent';
  targetDemographics: string;
  abTestingEnabled: boolean;
  analyticsTrackingId: string;
}

interface OfferPreview {
  title: string;
  description: string;
  discountPercentage: number;
  discountAmount: number;
  discountType: string;
  regularPrice: number;
  calculatedSavings: number;
  category: string;
  address: string;
  headerImage: string;
}

interface MapCoordinates {
  lat: number;
  lng: number;
}

const AdminCreateOffer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mapCoordinates, setMapCoordinates] = useState<MapCoordinates | null>(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  
  const getInitialFormData = (): AdminCreateOfferFormData => ({
    offerType: 'attraction',
    title: '',
    description: '',
    category: '',
    partnerId: '',
    address: '',
    regularPrice: 0,
    discountType: 'percentage',
    discountPercentage: 0,
    discountAmount: 0,
    freeGoodieDescription: '',
    redemptionMode: 'qr_upload',
    customRedemptionCode: '',
    redemptionUrl: '',
    redemptionType: 'unlimited',
    maxRedemptions: 0,
    userRedemptionLimit: 1,
    userRedemptionResetDays: 30,
    operatingHours: '',
    startDate: '',
    endDate: '',
    headerImage: '',
    logoImage: '',
    barcodeFile: null,
    qrFile: null,
    customTermsConditions: 'Offer subject to availability. Terms and conditions apply. Please present this offer at time of purchase. Cannot be combined with other promotions.',
    isFeatured: false,
    systemWidePromotion: false,
    priorityLevel: 'normal',
    targetDemographics: '',
    abTestingEnabled: false,
    analyticsTrackingId: ''
  });

  const [formData, setFormData] = useState<AdminCreateOfferFormData>(getInitialFormData());
  const [barcodePreviewUrl, setBarcodePreviewUrl] = useState<string>('');
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string>('');
  const [previewMode, setPreviewMode] = useState('mobile');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBarcode, setUploadingBarcode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [loadingOffer, setLoadingOffer] = useState(false);

  const categories = [
    'Attractions',
    'Shopping / Retail',
    'Beauty & Wellness',
    'Activities & Experiences',
    'Entertainment & Nightlife',
    'Transport & Mobility',
    'Local Services',
    'Kids & Family'
  ];

  // NEW: Restaurant type options for gastronomy offers
  const restaurantTypes = [
    'Austrian Cuisine',
    'Italian Cuisine',
    'Asian Cuisine',
    'Mediterranean Cuisine',
    'Vegan & Vegetarian',
    'Fast Food & Casual',
    'Fine Dining',
    'Café & Coffee Shop',
    'Bar & Cocktails',
    'Nightclub',
    'Pub & Beer Garden',
    'Wine Bar',
    'Bakery & Pastry'
  ];

  // Admin access check
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
      } else if (userProfile?.role !== 'admin') {
        setError('Access denied. This page is only accessible to administrators.');
        setTimeout(() => navigate('/executive-overview'), 2000);
      }
    }
  }, [user, userProfile, authLoading, navigate]);

  // Load partners for admin selection
  useEffect(() => {
    const loadPartners = async () => {
      if (userProfile?.role === 'admin') {
        setLoadingPartners(true);
        try {
          const partnersData = await getAllPartners();
          setPartners(partnersData);
          
          // NEW: Auto-select PART-033B partner as default for new offers
          if (!isEditMode && partnersData.length > 0) {
            const part033B = partnersData.find(
              p => p.partner_code === 'PART-033B' || p.business_name?.includes('033B') ||
                   p.contact_email?.includes('partner@travelheroes.at')
            );
            
            if (part033B) {
              console.log('✅ Found PART-033B partner, setting as default:', part033B.id);
              setFormData(prev => ({
                ...prev,
                partnerId: part033B.id
              }));
            } else {
              console.warn('⚠️ PART-033B partner not found in partners list');
            }
          }
        } catch (err: any) {
          console.error('Failed to load partners:', err);
          setError('Failed to load partners list');
        } finally {
          setLoadingPartners(false);
        }
      }
    };

    loadPartners();
  }, [userProfile, isEditMode]);

  // Check for edit mode
  useEffect(() => {
    const editId = searchParams.get('edit');
    const offerFromState = location.state?.offer as Offer;

    if (editId || offerFromState) {
      setIsEditMode(true);
      setEditingOfferId(editId || offerFromState?.id || null);
      
      if (offerFromState) {
        populateFormWithOffer(offerFromState);
      } else if (editId) {
        loadOfferForEdit(editId);
      }
    } else {
      setIsEditMode(false);
      setEditingOfferId(null);
      setFormData(getInitialFormData());
      setBarcodePreviewUrl('');
      setQrPreviewUrl('');
    }
  }, [searchParams, location.state]);

  const populateFormWithOffer = (offer: Offer) => {
    setFormData({
      offerType: offer.offerType || 'attraction',
      title: offer.title || '',
      description: offer.description || '',
      category: offer.category || '',
      partnerId: offer.partnerId || '',
      address: offer.address || '',
      regularPrice: offer.regularPrice || 0,
      discountType: (offer.discountType as 'percentage' | 'amount' | 'free_goodie' | 'one_plus_one') || 'percentage',
      discountPercentage: offer.discountPercentage || 0,
      discountAmount: offer.discountAmount || 0,
      freeGoodieDescription: offer.freeGoodieDescription || '',
      redemptionMode: (offer.redemptionMode as 'qr_upload' | 'barcode_upload' | 'auto_generate' | 'custom_code' | 'url') || 'auto_generate',
      customRedemptionCode: offer.customRedemptionCode || '',
      redemptionUrl: offer.customRedemptionCode && offer.redemptionMode === 'url' ? offer.customRedemptionCode : '',
      redemptionType: (offer.redemptionType as 'amount' | 'unlimited' | 'user_limit') || 'unlimited',
      maxRedemptions: offer.maxRedemptions || 0,
      userRedemptionLimit: 1,
      userRedemptionResetDays: 30,
      operatingHours: offer.operatingHours || '',
      startDate: '',
      endDate: offer.expiresAt ? new Date(offer.expiresAt).toISOString().split('T')[0] : '',
      headerImage: offer.imageUrl || '',
      logoImage: offer.businessLogoUrl || '',
      barcodeFile: null,
      qrFile: null,
      customTermsConditions: 'Offer subject to availability. Terms and conditions apply. Please present this offer at time of purchase. Cannot be combined with other promotions.',
      isFeatured: (offer as any).isFeatured || false,
      systemWidePromotion: false,
      priorityLevel: 'normal',
      targetDemographics: '',
      abTestingEnabled: false,
      analyticsTrackingId: ''
    });

    if (offer.barcodeUrl) {
      if (offer.redemptionMode === 'barcode_upload') {
        setBarcodePreviewUrl(offer.barcodeUrl);
      } else if (offer.redemptionMode === 'qr_upload') {
        setQrPreviewUrl(offer.barcodeUrl);
      }
    }

    if (offer.latitude && offer.longitude) {
      setMapCoordinates({
        lat: offer.latitude,
        lng: offer.longitude
      });
    }
  };

  const loadOfferForEdit = async (offerId: string) => {
    setLoadingOffer(true);
    setError('');
    
    try {
      const offer = await offerService.getOfferById(offerId);
      
      if (offer) {
        populateFormWithOffer(offer);
      } else {
        setError('Offer not found or access denied');
      }
    } catch (err: any) {
      setError(`Failed to load offer: ${err.message}`);
    } finally {
      setLoadingOffer(false);
    }
  };

  const handleInputChange = (field: keyof AdminCreateOfferFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'header' | 'logo' | 'barcode' | 'qr') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      if (type === 'header' || type === 'logo') {
        setUploadingImage(true);
        const folder = type === 'header' ? 'headers' : 'logos';
        const imageUrl = await offerService.uploadOfferImage(file, folder);
        
        if (type === 'header') {
          setFormData(prev => ({ ...prev, headerImage: imageUrl }));
        } else {
          setFormData(prev => ({ ...prev, logoImage: imageUrl }));
        }
      } else if (type === 'barcode' || type === 'qr') {
        setUploadingBarcode(true);
        const previewUrl = URL.createObjectURL(file);
        
        if (type === 'barcode') {
          setBarcodePreviewUrl(previewUrl);
          setFormData(prev => ({ ...prev, barcodeFile: file }));
        } else {
          setQrPreviewUrl(previewUrl);
          setFormData(prev => ({ ...prev, qrFile: file }));
        }
        
        setUploadingBarcode(false);
      }
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploadingImage(false);
      setUploadingBarcode(false);
    }
  };

  const handleAddressChange = async (address: string) => {
    setFormData(prev => ({ ...prev, address }));
    
    if (address.length > 10) {
      setGeocodingLoading(true);
      try {
        const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        
        if (googleMapsApiKey && googleMapsApiKey !== 'your-google-maps-api-key-here') {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.results && data.results.length > 0) {
              const location = data.results[0].geometry.location;
              setMapCoordinates({ lat: location.lat, lng: location.lng });
            } else {
              setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
            }
          } else {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          }
        } else {
          if (address.toLowerCase().includes('vienna') || address.toLowerCase().includes('wien')) {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          } else {
            setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
          }
        }
      } catch (err) {
        console.error('Geocoding failed:', err);
        setMapCoordinates({ lat: 48.2082, lng: 16.3738 });
      } finally {
        setGeocodingLoading(false);
      }
    } else {
      setMapCoordinates(null);
    }
  };

  const calculateSavings = (): number => {
    const { regularPrice, discountType, discountPercentage, discountAmount } = formData;
    
    if (!regularPrice || regularPrice <= 0) return 0;
    
    if (discountType === 'percentage' && discountPercentage > 0) {
      return (regularPrice * discountPercentage) / 100;
    } else if (discountType === 'amount' && discountAmount > 0) {
      return Math.min(discountAmount, regularPrice);
    } else if (discountType === 'one_plus_one') {
      return regularPrice;
    }
    
    return 0;
  };

  const calculateFinalPrice = (): number => {
    const { regularPrice, discountType } = formData;
    
    if (discountType === 'one_plus_one') {
      return regularPrice;
    }
    
    const savings = calculateSavings();
    return Math.max(0, regularPrice - savings);
  };

  const handlePublish = async () => {
    if (!user || userProfile?.role !== 'admin') {
      setError('Admin access required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      if (!formData.title || !formData.description || !formData.category) {
        throw new Error('Please fill in all required fields');
      }

      if (!formData.regularPrice || formData.regularPrice <= 0) {
        throw new Error('Please enter a valid regular price');
      }

      if (formData.redemptionMode === 'custom_code') {
        if (!formData.customRedemptionCode || formData.customRedemptionCode.trim().length < 3) {
          throw new Error('Custom redemption code must be at least 3 characters long');
        }
        if (!/^[A-Z0-9_-]+$/i.test(formData.customRedemptionCode)) {
          throw new Error('Custom redemption code can only contain letters, numbers, hyphens, and underscores');
        }
      }

      // NEW: Validation for URL redemption mode
      if (formData.redemptionMode === 'url') {
        if (!formData.redemptionUrl || formData.redemptionUrl.trim().length < 5) {
          throw new Error('Please enter a valid URL for redemption');
        }
        // Basic URL validation
        try {
          new URL(formData.redemptionUrl);
        } catch {
          throw new Error('Please enter a valid URL (e.g., https://example.com/offer)');
        }
      }

      let barcodeUrl = '';
      if (formData.redemptionMode === 'barcode_upload' && formData.barcodeFile) {
        barcodeUrl = await offerService.uploadBarcode(formData.barcodeFile);
      } else if (formData.redemptionMode === 'qr_upload' && formData.qrFile) {
        barcodeUrl = await offerService.uploadBarcode(formData.qrFile);
      }

      const offerData: CreateOfferData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        offerType: formData.offerType,
        partnerId: formData.partnerId || null,
        address: formData.address || null,
        regularPrice: formData.regularPrice,
        discountType: formData.discountType,
        discountPercentage: formData.discountPercentage || 0,
        discountAmount: formData.discountAmount || 0,
        freeGoodieDescription: formData.freeGoodieDescription || null,
        redemptionMode: formData.redemptionMode,
        customRedemptionCode: formData.redemptionMode === 'custom_code' ? formData.customRedemptionCode : (formData.redemptionMode === 'url' ? formData.redemptionUrl : undefined),
        redemptionUrl: formData.redemptionMode === 'url' ? formData.redemptionUrl : undefined,
        redemptionType: formData.redemptionType,
        maxRedemptions: formData.redemptionType === 'amount' ? formData.maxRedemptions : 0,
        userRedemptionLimit: formData.redemptionType === 'user_limit' ? formData.userRedemptionLimit : null,
        userRedemptionResetDays: formData.redemptionType === 'user_limit' ? formData.userRedemptionResetDays : null,
        operatingHours: formData.operatingHours || null,
        imageUrl: formData.headerImage || null,
        businessLogoUrl: formData.logoImage || null,
        barcodeUrl: (formData.redemptionMode === 'barcode_upload' || formData.redemptionMode === 'qr_upload') && barcodeUrl ? barcodeUrl : undefined,
        expiresAt: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        status: 'active',
        isFeatured: formData.isFeatured
      };

      if (isEditMode && editingOfferId) {
        await offerService.updateOffer(editingOfferId, offerData);
        setSuccess('Offer updated successfully!');
      } else {
        await offerService.createOffer(offerData);
        setSuccess('Offer created successfully!');
      }

      setTimeout(() => navigate('/offer-management'), 1500);
    } catch (error: any) {
      if (error.message === 'Not authenticated') {
        setError('Authentication expired. Please log in again.');
        setTimeout(() => navigate('/auth/login'), 2000);
      } else {
        setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} offer`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOfferPreview = (): OfferPreview => {
    return {
      title: formData.title || 'Offer Title Preview',
      description: formData.description || 'Offer description will appear here...',
      discountPercentage: formData.discountPercentage || 0,
      discountAmount: formData.discountAmount || 0,
      discountType: formData.discountType,
      regularPrice: formData.regularPrice || 0,
      calculatedSavings: calculateSavings(),
      category: formData.category || 'Category',
      address: formData.address || 'Location',
      headerImage: formData.headerImage || 'https://images.unsplash.com/photo-1708782340354-f77e721d1ef8'
    };
  };

  const renderMapPreview = () => {
    if (!formData.address) {
      return (
        <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Enter address to see map preview
            </p>
          </div>
        </div>
      );
    }

    if (geocodingLoading) {
      return (
        <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">
              Loading map preview...
            </p>
          </div>
        </div>
      );
    }

    if (mapCoordinates) {
      const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (!googleMapsApiKey || googleMapsApiKey === 'your-google-maps-api-key-here') {
        return (
          <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 font-medium">
                Google Maps API Key Required
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="bg-muted rounded-lg h-48 overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(formData.address)}&zoom=15&maptype=roadmap`}
            title="Location Preview"
          />
        </div>
      );
    }

    return (
      <div className="bg-muted rounded-lg h-48 flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Address: {formData.address}
          </p>
        </div>
      </div>
    );
  };

  const preview = renderOfferPreview();

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Edit Offer (Admin)' : 'Create Offer (Admin)'} - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="Administrative offer creation with enhanced controls" />
      </Helmet>

      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Admin Header with Badge */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/offer-management')}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Offer Management</span>
                </Button>
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-foreground">
                      {isEditMode ? 'Edit Offer (Admin)' : 'Create New Offer (Admin)'}
                    </h1>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin Only
                    </span>
                  </div>
                  <p className="text-muted-foreground">
                    {isEditMode 
                      ? 'Update offer with administrative privileges and advanced configuration' :'Create offers with administrative controls, partner assignment, and system-level settings'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-8 gap-8">
            <div className="lg:col-span-5">
              <div className="bg-card border border-border rounded-lg mb-6">
                <div className="flex border-b border-border overflow-x-auto">
                  {[
                    { id: 'basic', label: 'Basic Details', icon: Target },
                    { id: 'location', label: 'Location & Hours', icon: MapPin },
                    { id: 'pricing', label: 'Pricing & Validity', icon: Calculator },
                    { id: 'redemption', label: 'Redemption Mode', icon: Settings },
                    { id: 'admin', label: 'Admin Controls', icon: Shield }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  ))}
                </div>

                <div className="p-6">
                  {activeTab === 'basic' && (
                    <div className="space-y-6">
                      {/* NEW: Offer Type Selection - FIRST QUESTION */}
                      <div>
                        <label className="block text-sm font-medium mb-3">
                          Offer Type * <span className="text-xs text-muted-foreground">(Determines mobile app categorization)</span>
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button
                            type="button"
                            onClick={() => handleInputChange('offerType', 'restaurant')}
                            className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all ${
                              formData.offerType === 'restaurant' ?'border-primary bg-primary/10 text-primary font-medium shadow-md' :'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                          >
                            <span className="text-4xl mb-3">🍽️</span>
                            <span className="text-lg font-semibold mb-1">Restaurant / Gastronomy</span>
                            <span className="text-xs text-muted-foreground text-center">
                              Restaurants, cafés, bars, and food establishments
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleInputChange('offerType', 'attraction')}
                            className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all ${
                              formData.offerType === 'attraction' ?'border-primary bg-primary/10 text-primary font-medium shadow-md' :'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                          >
                            <span className="text-4xl mb-3">🎭</span>
                            <span className="text-lg font-semibold mb-1">Attraction / Standard Offer</span>
                            <span className="text-xs text-muted-foreground text-center">
                              Entertainment, shopping, activities, and all other offers
                            </span>
                          </button>
                        </div>
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-700">
                            <strong>Admin Note:</strong> This classification affects how offers appear in the mobile app. 
                            Restaurants are shown in a dedicated "Dining" section, while attractions appear in the main "Explore" section.
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Offer Title *</label>
                        <Input
                          placeholder="e.g., Traditional Viennese Schnitzel Experience"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Description *</label>
                        <textarea
                          placeholder="Detailed description of the offer..."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>

                      {/* CONDITIONAL: Show category picker ONLY for attractions */}
                      {formData.offerType === 'attraction' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Category *</label>
                          <Select
                            value={formData.category}
                            onChange={(value) => handleInputChange('category', value)}
                            placeholder="Select category"
                            options={categories.map(category => ({
                              value: category,
                              label: category
                            }))}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Choose the category that best describes this attraction or activity
                          </p>
                        </div>
                      )}

                      {/* NEW: Show restaurant type picker ONLY for restaurants */}
                      {formData.offerType === 'restaurant' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Restaurant Type * <span className="text-xs text-muted-foreground">(Cuisine or establishment type)</span>
                          </label>
                          <Select
                            value={formData.category}
                            onChange={(value) => handleInputChange('category', value)}
                            placeholder="Select restaurant type"
                            options={restaurantTypes.map(type => ({
                              value: type,
                              label: type
                            }))}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the type of cuisine, dining experience, or nightlife establishment
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium mb-2">Header Image</label>
                        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            {uploadingImage ? 'Uploading...' : 'Upload header image'}
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'header')}
                            className="hidden"
                            id="header-image-upload"
                            disabled={uploadingImage}
                          />
                          <Button variant="outline" onClick={() => document.getElementById('header-image-upload')?.click()} disabled={uploadingImage}>
                            Choose File
                          </Button>
                        </div>
                        {formData.headerImage && (
                          <div className="mt-4">
                            <img src={formData.headerImage} alt="Header preview" className="w-32 h-32 object-cover rounded-lg" />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Logo Image</label>
                        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            {uploadingImage ? 'Uploading...' : 'Upload business logo'}
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, 'logo')}
                            className="hidden"
                            id="logo-image-upload"
                            disabled={uploadingImage}
                          />
                          <Button variant="outline" onClick={() => document.getElementById('logo-image-upload')?.click()} disabled={uploadingImage}>
                            Choose Logo
                          </Button>
                        </div>
                        {formData.logoImage && (
                          <div className="mt-4">
                            <img src={formData.logoImage} alt="Logo preview" className="w-16 h-16 object-cover rounded-lg" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'location' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Address *</label>
                        <Input
                          placeholder="e.g., 123 Main Street, Vienna, Austria"
                          value={formData.address}
                          onChange={(e) => handleAddressChange(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      <OpeningHoursSelector
                        value={formData.operatingHours}
                        onChange={(value) => handleInputChange('operatingHours', value)}
                      />

                      <div className="bg-muted/50 rounded-lg p-4">
                        <h3 className="font-medium mb-2">Map Preview</h3>
                        {renderMapPreview()}
                      </div>
                    </div>
                  )}

                  {activeTab === 'pricing' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Regular Price (€) *</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="29.90"
                          value={formData.regularPrice || ''}
                          onChange={(e) => handleInputChange('regularPrice', parseFloat(e.target.value) || 0)}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-3">Discount Type *</label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { value: 'percentage', label: 'Percentage Off', icon: '%' },
                            { value: 'amount', label: 'Fixed Amount', icon: '€' },
                            { value: 'free_goodie', label: 'Free Goodie', icon: '🎁' },
                            { value: 'one_plus_one', label: '1 + 1 Offer', icon: '2x' }
                          ].map(type => (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => handleInputChange('discountType', type.value)}
                              className={`flex items-center justify-center space-x-2 p-3 rounded-lg border-2 transition-all ${
                                formData.discountType === type.value
                                  ? 'border-primary bg-primary/10 text-primary font-medium' :'border-border hover:border-primary/50'
                              }`}
                            >
                              <span className="text-lg">{type.icon}</span>
                              <span className="text-sm">{type.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {formData.discountType === 'percentage' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Discount Percentage *</label>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            placeholder="20"
                            value={formData.discountPercentage || ''}
                            onChange={(e) => handleInputChange('discountPercentage', parseInt(e.target.value) || 0)}
                          />
                        </div>
                      )}

                      {formData.discountType === 'amount' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Discount Amount (€) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="5.00"
                            value={formData.discountAmount || ''}
                            onChange={(e) => handleInputChange('discountAmount', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      )}

                      {formData.discountType === 'free_goodie' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Free Goodie Description *</label>
                          <Input
                            placeholder="e.g., Free dessert with main course"
                            value={formData.freeGoodieDescription}
                            onChange={(e) => handleInputChange('freeGoodieDescription', e.target.value)}
                          />
                        </div>
                      )}

                      <div className="bg-muted/50 rounded-lg p-4">
                        <h3 className="font-medium mb-3">Price Calculation</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Regular Price:</span>
                            <span>€{formData.regularPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>You Save:</span>
                            <span>€{calculateSavings().toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t pt-2">
                            <span>Final Price:</span>
                            <span>€{calculateFinalPrice().toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-muted/50 rounded-lg p-4">
                        <h3 className="font-medium mb-3">Redemption Limit *</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                          {[
                            { value: 'unlimited', label: 'Unlimited', icon: '∞' },
                            { value: 'amount', label: 'Limited', icon: '#' },
                            { value: 'user_limit', label: 'Per User', icon: '👤' }
                          ].map(type => (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => handleInputChange('redemptionType', type.value)}
                              className={`flex flex-col items-center space-y-2 p-4 rounded-lg border-2 transition-all ${
                                formData.redemptionType === type.value
                                  ? 'border-primary bg-primary/10 text-primary font-medium' :'border-border hover:border-primary/50'
                              }`}
                            >
                              <span className="text-2xl">{type.icon}</span>
                              <span className="text-sm font-medium">{type.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {formData.redemptionType === 'amount' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Maximum Redemptions</label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="100"
                            value={formData.maxRedemptions || ''}
                            onChange={(e) => handleInputChange('maxRedemptions', parseInt(e.target.value) || 0)}
                          />
                        </div>
                      )}

                      {formData.redemptionType === 'user_limit' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Redemptions per User</label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="1"
                                value={formData.userRedemptionLimit || ''}
                                onChange={(e) => handleInputChange('userRedemptionLimit', parseInt(e.target.value) || 1)}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-2">Reset Period (days)</label>
                              <Input
                                type="number"
                                min="1"
                                placeholder="30"
                                value={formData.userRedemptionResetDays || ''}
                                onChange={(e) => handleInputChange('userRedemptionResetDays', parseInt(e.target.value) || 30)}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Start Date</label>
                          <Input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">End Date</label>
                          <Input
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => handleInputChange('endDate', e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Custom Terms & Conditions</label>
                        <textarea
                          placeholder="Enter custom terms..."
                          value={formData.customTermsConditions}
                          onChange={(e) => handleInputChange('customTermsConditions', e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'redemption' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium mb-3">Redemption Mode *</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { value: 'qr_upload', label: 'Upload QR', icon: '📱' },
                            { value: 'barcode_upload', label: 'Upload Barcode', icon: '📊' },
                            { value: 'custom_code', label: 'Custom Code', icon: '✏️' },
                            { value: 'url', label: 'URL', icon: '🔗' }
                          ].map(mode => (
                            <button
                              key={mode.value}
                              type="button"
                              onClick={() => {
                                handleInputChange('redemptionMode', mode.value);
                                if (mode.value === 'barcode_upload') {
                                  setQrPreviewUrl('');
                                } else if (mode.value === 'qr_upload') {
                                  setBarcodePreviewUrl('');
                                } else {
                                  setBarcodePreviewUrl('');
                                  setQrPreviewUrl('');
                                }
                              }}
                              className={`flex flex-col items-center space-y-2 p-4 rounded-lg border-2 transition-all ${
                                formData.redemptionMode === mode.value
                                  ? 'border-primary bg-primary/10 text-primary font-medium' :'border-border hover:border-primary/50'
                              }`}
                            >
                              <span className="text-2xl">{mode.icon}</span>
                              <span className="text-sm font-medium">{mode.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {formData.redemptionMode === 'qr_upload' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Upload QR Code</label>
                          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground mb-2">
                              {uploadingBarcode ? 'Uploading...' : 'Upload QR code image'}
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'qr')}
                              className="hidden"
                              id="qr-upload"
                              disabled={uploadingBarcode}
                            />
                            <Button variant="outline" onClick={() => document.getElementById('qr-upload')?.click()} disabled={uploadingBarcode}>
                              Choose QR Code
                            </Button>
                          </div>
                          
                          {(formData.qrFile || qrPreviewUrl) && (
                            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                              <p className="text-sm font-medium mb-2">QR Code Preview:</p>
                              <img 
                                src={qrPreviewUrl} 
                                alt="QR Code preview" 
                                className="w-32 h-32 object-contain bg-white rounded border border-border" 
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {formData.redemptionMode === 'barcode_upload' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Upload Barcode</label>
                          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground mb-2">
                              {uploadingBarcode ? 'Uploading...' : 'Upload barcode image'}
                            </p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'barcode')}
                              className="hidden"
                              id="barcode-upload"
                              disabled={uploadingBarcode}
                            />
                            <Button variant="outline" onClick={() => document.getElementById('barcode-upload')?.click()} disabled={uploadingBarcode}>
                              Choose Barcode
                            </Button>
                          </div>
                          
                          {(formData.barcodeFile || barcodePreviewUrl) && (
                            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                              <p className="text-sm font-medium mb-2">Barcode Preview:</p>
                              <img 
                                src={barcodePreviewUrl} 
                                alt="Barcode preview" 
                                className="w-32 h-32 object-contain bg-white rounded border border-border" 
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {formData.redemptionMode === 'custom_code' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Custom Redemption Code</label>
                          <Input
                            placeholder="e.g., VIENNA2024"
                            value={formData.customRedemptionCode}
                            onChange={(e) => handleInputChange('customRedemptionCode', e.target.value.toUpperCase())}
                          />
                        </div>
                      )}

                      {formData.redemptionMode === 'url' && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Redemption URL *</label>
                          <Input
                            placeholder="e.g., https://yourwebsite.com/special-offer"
                            value={formData.redemptionUrl}
                            onChange={(e) => handleInputChange('redemptionUrl', e.target.value)}
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Enter the clickable URL where users will be directed to redeem this offer. This could be a link to your website, booking page, or partner platform.
                          </p>
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-700">
                              <strong>Admin Note:</strong> URL redemption mode allows partners to direct users to external redemption pages without revealing specific business purposes.
                            </p>
                          </div>
                          {formData.redemptionUrl && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-xs text-green-700">
                                <strong>Preview:</strong> Users will be redirected to: {formData.redemptionUrl}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'admin' && (
                    <div className="space-y-6">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start space-x-3">
                          <Shield className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-red-900 mb-1">Administrative Controls</h3>
                            <p className="text-sm text-red-700">
                              These advanced settings are only available to administrators and provide enhanced control over offer behavior and partner assignment.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          <div className="flex items-center space-x-2">
                            <Users className="w-4 h-4" />
                            <span>Partner Assignment</span>
                          </div>
                        </label>
                        <Select
                          value={formData.partnerId}
                          onChange={(value) => handleInputChange('partnerId', value)}
                          placeholder={loadingPartners ? "Loading partners..." : "Select partner (optional)"}
                          options={[
                            { value: '', label: 'No Partner (System Offer)' },
                            ...partners.map(partner => ({
                              value: partner.id,
                              label: `${partner.business_name || 'Unnamed Partner'}${partner.partner_code ? ` (${partner.partner_code})` : ''}`
                            }))
                          ]}
                          disabled={loadingPartners}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Assign this offer to a specific partner or leave empty for system-wide offers
                        </p>
                        {formData.partnerId && partners.find(p => p.id === formData.partnerId)?.partner_code === 'PART-033B' && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-700">
                              <strong>🏢 Internal System Partner:</strong> PART-033B is the default internal partner used for all system-generated offers.
                            </p>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Priority Level</label>
                        <Select
                          value={formData.priorityLevel}
                          onChange={(value) => handleInputChange('priorityLevel', value)}
                          placeholder="Select priority"
                          options={[
                            { value: 'normal', label: 'Normal Priority' },
                            { value: 'high', label: 'High Priority' },
                            { value: 'urgent', label: 'Urgent Priority' }
                          ]}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Higher priority offers appear first in listings
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Target Demographics</label>
                        <Input
                          placeholder="e.g., Young professionals, Families with children"
                          value={formData.targetDemographics}
                          onChange={(e) => handleInputChange('targetDemographics', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Specify target user demographics for analytics tracking
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Analytics Tracking ID</label>
                        <Input
                          placeholder="Custom tracking ID for analytics"
                          value={formData.analyticsTrackingId}
                          onChange={(e) => handleInputChange('analyticsTrackingId', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Optional custom ID for advanced analytics tracking
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                          <div>
                            <label className="font-medium text-sm">System-Wide Promotion</label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Display this offer prominently across all channels
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.systemWidePromotion}
                            onChange={(e) => handleInputChange('systemWidePromotion', e.target.checked)}
                            className="w-5 h-5 rounded border-border"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                          <div>
                            <label className="font-medium text-sm">A/B Testing</label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Enable A/B testing for this offer
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.abTestingEnabled}
                            onChange={(e) => handleInputChange('abTestingEnabled', e.target.checked)}
                            className="w-5 h-5 rounded border-border"
                          />
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Admin Privileges</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Override partner offer limits</li>
                          <li>• Create system-level promotional campaigns</li>
                          <li>• Access advanced analytics configuration</li>
                          <li>• Bypass standard validation rules when necessary</li>
                        </ul>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                        <div>
                          <label className="font-medium text-sm flex items-center space-x-2">
                            <span className="text-xl">⭐</span>
                            <span>Featured Offer</span>
                          </label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Mark this offer as featured for prominent display in the mobile app
                          </p>
                          <p className="text-xs text-yellow-700 font-medium mt-1">
                            ✨ Featured offers appear first in listings and have special visual highlighting
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.isFeatured}
                          onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                          className="w-5 h-5 rounded border-yellow-400 accent-yellow-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/offer-management')}
                  >
                    Cancel
                  </Button>
                  <div className="flex items-center space-x-3">
                    <Button variant="outline" onClick={handlePublish} disabled={loading}>
                      {loading ? 'Saving...' : 'Save as Draft'}
                    </Button>
                    <Button onClick={handlePublish} disabled={loading}>
                      {loading 
                        ? (isEditMode ? 'Updating...' : 'Publishing...') 
                        : (isEditMode ? 'Update Offer' : 'Publish Offer')
                      }
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="sticky top-24">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Live Preview</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPreviewMode('mobile')}
                        className={`px-3 py-1 rounded text-xs ${
                          previewMode === 'mobile' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}
                      >
                        Mobile
                      </button>
                      <button
                        onClick={() => setPreviewMode('desktop')}
                        className={`px-3 py-1 rounded text-xs ${
                          previewMode === 'desktop' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}
                      >
                        Desktop
                      </button>
                    </div>
                  </div>

                  {previewMode === 'mobile' && (
                    <div className="bg-muted rounded-lg p-4 max-w-sm mx-auto">
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="h-32 bg-gray-200 flex items-center justify-center">
                          {preview.headerImage ? (
                            <img src={preview.headerImage} alt="Offer preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Header Image</span>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            {formData.discountType === 'percentage' ? (
                              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                                {preview.discountPercentage}% OFF
                              </span>
                            ) : formData.discountType === 'amount' ? (
                              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                                €{preview.discountAmount} OFF
                              </span>
                            ) : formData.discountType === 'one_plus_one' ? (
                              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                                1+1 OFFER
                              </span>
                            ) : (
                              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                                FREE GOODIE
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">{preview.category}</span>
                          </div>
                          <h4 className="font-semibold text-sm mb-1">{preview.title}</h4>
                          <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                            {preview.description}
                          </p>
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Regular:</span>
                              <span className="line-through">€{preview.regularPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold">
                              <span>Final Price:</span>
                              <span className="text-green-600">€{calculateFinalPrice().toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {previewMode === 'desktop' && (
                    <div className="bg-muted rounded-lg p-4">
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="h-40 bg-gray-200 flex items-center justify-center">
                          {preview.headerImage ? (
                            <img src={preview.headerImage} alt="Offer preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm text-gray-400">Header Image</span>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            {formData.discountType === 'percentage' ? (
                              <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded">
                                {preview.discountPercentage}% OFF
                              </span>
                            ) : formData.discountType === 'amount' ? (
                              <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded">
                                €{preview.discountAmount} OFF
                              </span>
                            ) : formData.discountType === 'one_plus_one' ? (
                              <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded">
                                1+1 OFFER
                              </span>
                            ) : (
                              <span className="bg-primary text-primary-foreground text-sm px-3 py-1 rounded">
                                FREE GOODIE
                              </span>
                            )}
                            <span className="text-sm text-muted-foreground">{preview.category}</span>
                          </div>
                          <h4 className="font-semibold mb-2">{preview.title}</h4>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                            {preview.description}
                          </p>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Regular:</span>
                              <span className="line-through">€{preview.regularPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-bold">
                              <span>Final Price:</span>
                              <span className="text-green-600">€{calculateFinalPrice().toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 text-xs text-muted-foreground">
                    Preview updates in real-time as you fill the form
                  </div>

                  {formData.systemWidePromotion && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs text-blue-700 font-medium">
                        🌟 This offer will be featured system-wide
                      </p>
                    </div>
                  )}

                  {formData.isFeatured && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded">
                      <p className="text-xs text-yellow-800 font-medium flex items-center">
                        <span className="text-base mr-1">⭐</span> This is a featured offer (shown prominently in mobile app)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loadingOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-medium">Loading offer data...</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCreateOffer;